import { validateRichText } from './scriptRichText';
import {validateDualDialogue} from './scriptDualDialogue';
import {validateAlternates} from './scriptAlternates';
import {validateRevisions,type ScriptRevisions} from './scriptRevisions';
import type { ScriptBlockData } from '../components/story/ScriptBlock';
import type { TitlePageData } from '../components/story/TitlePage';
import type { ScriptRecoveryJournal } from './scriptRecovery';

export interface ScriptDocumentData {
    schemaVersion: 1;
    blockNotes: Record<string, string>;
    settings: { showSceneNumbers: boolean; showScriptProperties: boolean };
    revisions?:ScriptRevisions;
    // Imported XML remains an original file, not an export of later editor edits.
    sourceFDX?: { fileName: string; xml: string; paragraphIds?: string[] };
}

export function validateScriptDocument(value: unknown): ScriptDocumentData {
    if (value == null) return { schemaVersion: 1, blockNotes: {}, settings: { showSceneNumbers: false, showScriptProperties: true } };
    const document = value as ScriptDocumentData;
    if (typeof value !== 'object' || Array.isArray(value) || document.schemaVersion !== 1
        || !document.blockNotes || typeof document.blockNotes !== 'object' || Array.isArray(document.blockNotes)
        || Object.entries(document.blockNotes).some(([id, note]) => !id || typeof note !== 'string')
        || !document.settings || typeof document.settings !== 'object' || Array.isArray(document.settings)
        || typeof document.settings.showSceneNumbers !== 'boolean' || typeof document.settings.showScriptProperties !== 'boolean') {
        throw new Error('Unsupported or invalid screenplay document settings.');
    }
    if (document.sourceFDX !== undefined && (!document.sourceFDX || typeof document.sourceFDX !== 'object'
        || Array.isArray(document.sourceFDX) || typeof document.sourceFDX.fileName !== 'string'
        || typeof document.sourceFDX.xml !== 'string' || new TextEncoder().encode(document.sourceFDX.xml).length > 5 * 1024 * 1024)) {
        throw new Error('Invalid preserved Final Draft file.');
    }
    const bindings = document.sourceFDX?.paragraphIds;
    validateRevisions(document.revisions);
    if (bindings !== undefined && (!Array.isArray(bindings) || bindings.some(id => typeof id !== 'string' || !id)
        || new Set(bindings).size !== bindings.length)) throw new Error('Invalid Final Draft paragraph bindings.');
    return document; // Retain opaque fields of this schema; never silently strip them.
}

export interface ScriptSnapshot {
    blocks: ScriptBlockData[];
    titlePage: TitlePageData;
    document?: ScriptDocumentData;
}
export type SaveStatus = 'saved' | 'saving' | 'error';

export class ScriptConflictError extends Error {
    constructor() { super('This screenplay changed in another session. Download your draft, then reload the cloud version to resolve the conflict.'); }
}

// One queue per loaded project. Snapshots are detached from editable state and
// acknowledged only after the writer confirms the row was updated.
export class ScriptSaveSession {
    private acknowledged: string;
    private latest: string;
    private inFlight = false;
    private writable = true;
    private closed = false;
    private timer: ReturnType<typeof setTimeout> | undefined;
    private failed = false;
    private revision: number;
    private conflict = false;
    private backupGeneration = 0;
    private acknowledgedBackup = 0;

    constructor(
        readonly projectId: string,
        initial: ScriptSnapshot,
        private write: (projectId: string, snapshot: ScriptSnapshot, revision: number, backupBeforeSave: boolean) => Promise<number>,
        private report: (status: SaveStatus, error?: string) => void,
        private delay = 1000,
        private options: { revision: number; journal?: ScriptRecoveryJournal; onRecoveryError?: (message?: string) => void } = { revision: 0 },
    ) {
        this.acknowledged = this.latest = JSON.stringify(initial);
        this.revision = options.revision;
    }

    get pending() { return this.inFlight || this.latest !== this.acknowledged; }
    get conflicted() { return this.conflict; }
    get snapshot(): ScriptSnapshot { return JSON.parse(this.latest) as ScriptSnapshot; }

    restore(snapshot: ScriptSnapshot, inheritedId?: string) {
        this.backupGeneration++;
        if (inheritedId) this.options.journal?.inherit(inheritedId);
        this.schedule(snapshot);
    }

    schedule(snapshot: ScriptSnapshot) {
        if (this.closed) return;
        const next = JSON.stringify(snapshot);
        if (next === this.latest) return;
        this.latest = next;
        this.journal();
        if (this.conflict) return; // Further typing must not turn a conflict into a blind retry.
        this.failed = false;
        this.queue();
    }

    setWritable(writable: boolean) {
        this.writable = writable;
        if (!writable) this.cancelTimer();
        else if (this.pending && !this.failed) this.queue();
    }

    retry() {
        if (!this.closed && this.writable && !this.conflict) {
            this.failed = false;
            this.cancelTimer();
            if (this.pending) this.report('saving');
            void this.save();
        }
    }

    close() {
        this.closed = true;
        this.cancelTimer();
    }

    private cancelTimer() {
        if (this.timer !== undefined) clearTimeout(this.timer);
        this.timer = undefined;
    }

    private journal() {
        if (!this.options.journal) return;
        try {
            if (this.latest === this.acknowledged && !this.inFlight) this.options.journal.acknowledge();
            else this.options.journal.write(this.snapshot, this.revision, this.backupGeneration !== this.acknowledgedBackup);
            if (!this.closed) this.options.onRecoveryError?.();
        } catch {
            if (!this.closed) this.options.onRecoveryError?.('Local recovery could not be stored. Keep this tab open until the cloud save succeeds, or download your draft.');
        }
    }

    private queue() {
        this.cancelTimer();
        if (this.closed || !this.writable) return;
        if (!this.pending) { this.report('saved'); return; }
        this.report('saving');
        this.timer = setTimeout(() => {
            this.timer = undefined;
            void this.save();
        }, this.delay);
    }

    private async save() {
        if (this.closed || !this.writable || this.inFlight || !this.pending) return;
        const sent = this.latest;
        const sentBackup = this.backupGeneration;
        this.inFlight = true;
        try {
            const revision = await this.write(this.projectId, JSON.parse(sent) as ScriptSnapshot,
                this.revision, sentBackup !== this.acknowledgedBackup);
            if (!Number.isSafeInteger(revision) || revision < this.revision) throw new Error('The cloud save returned an invalid revision.');
            this.revision = revision;
            this.acknowledged = sent;
            this.acknowledgedBackup = sentBackup;
            this.failed = false;
        } catch (error) {
            this.failed = true;
            this.conflict = error instanceof ScriptConflictError;
            if (!this.closed) this.report('error',
                error instanceof Error ? error.message : 'Your changes could not be saved.');
        } finally {
            this.inFlight = false;
            this.journal();
            if (!this.closed && !this.failed) {
                if (!this.pending) this.report('saved');
                else if (this.timer === undefined) this.queue();
            }
        }
    }
}

export function validateScriptBlocks(value: unknown): ScriptBlockData[] {
    if (value == null) return [];
    if (!Array.isArray(value)) throw new Error('The stored screenplay is not a valid block list.');
    const ids = new Set<string>();
    const nestedIds = new Set<string>();
    for (const block of value) {
        if (!block || typeof block.id !== 'string' || !block.id || ids.has(block.id)
            || typeof block.type !== 'string' || typeof block.content !== 'string') {
            throw new Error('The stored screenplay contains invalid or duplicate blocks.');
        }
        validateRichText(block.richText, block.content);
        validateAlternates(block.alternates);
        if(block.type==='dual-dialogue' && block.alternates)throw new Error('Alternatives belong to individual dialogue paragraphs.');
        if (block.dualDialogue!==undefined) {
            if (block.type!=='dual-dialogue' || block.richText!==undefined) throw new Error('Invalid dual dialogue block.');
            validateDualDialogue(block.dualDialogue,block.content);
            for (const part of [...block.dualDialogue.left,...block.dualDialogue.right]) {
                if (nestedIds.has(part.id) || value.some(root => root?.id===part.id)) throw new Error('Duplicate dual paragraph identity.');
                nestedIds.add(part.id);
            }
        }
        ids.add(block.id);
    }
    return value as ScriptBlockData[];
}
